import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="forgot-password-page">
      <div className="px-8 py-6">
        <div className="inline-flex items-center gap-2 text-slate-500">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-widest">Password reset</span>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                <MailCheck className="h-7 w-7 text-emerald-600" />
              </div>
              <div className="font-display text-xl font-bold tracking-tight text-slate-900">Check your email</div>
              <p className="mt-2 text-sm text-slate-600">
                If an account exists for <span className="font-medium text-slate-900">{email}</span>, we've sent a
                password reset link. It expires in 30 minutes.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Didn't get it? Check your spam folder, or ask your admin to send you a reset link from the Employees page.
              </p>
              <Link to="/login" data-testid="back-to-login-link"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="font-display text-2xl font-bold tracking-tight text-slate-900">Forgot your password?</div>
                <p className="mt-1 text-sm text-slate-500">
                  Enter your login email and we'll send you a link to reset it.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                <label className="block">
                  <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Email</span>
                  <input
                    type="email" required autoFocus value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email"
                    className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
                  />
                </label>
                <button
                  type="submit" disabled={submitting}
                  data-testid="forgot-password-submit"
                  className="w-full h-12 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </button>
              </form>
              <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4">
                <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
